// SF6 — whether the listing, not the ad, is why the ad does not sell.
//
// One split does all the work. Shown and not clicked failed in the search
// results — image, title, price, delivery badge. Clicked and not bought failed
// on the page — bullets, A+, image count, price. Different teams, different
// fixes, and Amazon shows both as one bad ACOS.
//
// Worded as a likely cause throughout. The figures prove what shoppers did;
// they cannot prove why.
import React from 'react';
import s from '../AdsHealth.module.css';
import { money, pct } from '../format';

const TONE = {
  'Seen, not clicked': 'warn',
  'Clicked, not bought': 'bad',
  Neither: 'bad',
};

export default function Diagnosis({ data, currency }) {
  if (!data) return <p className={s.empty}>Loading…</p>;
  if (!(data.rows || []).length) return <p className={s.empty}>{data.note}</p>;

  return (
    <div>
      <p className={s.coverage}>
        Measured against the median of{' '}
        {data.benchmark?.basis === 'category'
          ? 'your other products in the same category'
          : `your other advertised products (${data.benchmark?.products})`}
        . Your own products are the benchmark, never a market average — a brass
        showpiece and a phone case have nothing to say about each other's click rate.
      </p>
      {data.too_small_note && <p className={s.coverage}>{data.too_small_note}</p>}

      <div className={s.tableWrap}>
        <table className={s.table}>
          <thead>
            <tr>
              <th>Product</th><th>Reading</th><th className={s.right}>CTR</th>
              <th className={s.right}>CVR</th><th className={s.right}>Spend</th>
              <th>Likely cause</th><th>Why</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.asin} className={r.pattern ? '' : s.rowMuted}>
                <td>
                  <span className={s.asin}>{r.asin}</span>
                  {r.title && <div className={s.thin}>{r.title.slice(0, 60)}</div>}
                </td>
                <td>
                  {r.pattern
                    ? <span className={s[TONE[r.pattern] || 'muted']}>{r.pattern}</span>
                    : <span className={s.thin}>Nothing to flag</span>}
                </td>
                <td className={s.right}>
                  {pct(r.ctr, 2)}
                  <div className={s.thin}>vs {pct(r.benchmark_ctr, 2)}</div>
                </td>
                <td className={s.right}>
                  {pct(r.cvr, 1)}
                  <div className={s.thin}>vs {pct(r.benchmark_cvr, 1)}</div>
                </td>
                <td className={s.right}>{money(r.spend, currency)}</td>
                <td className={s.thin}>{(r.likely_cause || []).join(', ') || '—'}</td>
                <td className={s.thin} style={{ maxWidth: 420 }}>{r.why || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className={s.coverage}>{data.wording_note}</p>

      {!!(data.before_after || []).length && (
        <>
          <h3 className={s.sectionTitle}>Before and after a listing change</h3>
          <p className={s.coverage}>{data.before_after_note}</p>
          <div className={s.tableWrap}>
            <table className={s.table}>
              <thead>
                <tr>
                  <th>Product</th><th>Published</th><th className={s.right}>CTR</th>
                  <th className={s.right}>CVR</th><th className={s.right}>ACOS</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {data.before_after.map((r) => (
                  <tr key={r.asin}>
                    <td><span className={s.asin}>{r.asin}</span></td>
                    <td className={s.thin}>{r.published_on}</td>
                    {r.comparable ? (
                      <>
                        <td className={s.right}>
                          {pct(r.before?.ctr, 2)} → {pct(r.after?.ctr, 2)}
                        </td>
                        <td className={s.right}>
                          {pct(r.before?.cvr, 1)} → {pct(r.after?.cvr, 1)}
                        </td>
                        <td className={s.right}>
                          {pct(r.before?.acos)} → {pct(r.after?.acos)}
                        </td>
                        <td className={s.thin}>{r.note}</td>
                      </>
                    ) : (
                      <td colSpan={4} className={s.thin}>{r.why}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
